/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import {
	getGroupedMultiSelectDisplayValueHelper,
	getMultiSelectDisplayValueHelper,
	groupedMultiSelectSelectionChangedHelper,
	multiSelectSelectionChangedHelper,
	selectAllItems,
	toggleAllMultiSelectSelectionGroupedHelper,
	toggleAllMultiSelectSelectionHelper,
} from 'app/shared/helpers/multi-select-dropdown-helper';
import { Filter } from 'app/shared/models/filter.model';
import { Subject, Subscription } from 'rxjs';
import { ListFilterIntermediaryService } from './list-filter-intermediary-service.component';

@Component({
	selector: 'filter-fields-base',
	template: '',
	standalone: false,
})
export class FilterFieldsBaseComponent {
	dataForm: FormGroup; // the fields defining the filter to apply
	// stores the previous values of the multi-select dropdowns 'All' option
	previousValues = new Map<string, any>();
	@Output() filterChanged = new EventEmitter<Array<Filter>>();

	subscription: Subscription;
	private ngUnsubscribe: Subject<boolean> = new Subject();

	constructor(protected listFilterIntermediaryService: ListFilterIntermediaryService) {
		// Subscribe to the intermediary service
		this.subscription = this.listFilterIntermediaryService.getParentMethod().subscribe((message) => {
			// Call your method here
			this.applyFilter();
		});
	}

	ngOnInit() {
		this.filterChanged.subscribe((params) => {
			this.applyFilter();
		});
	}

	ngAfterViewInit() {}

	ngOnDestroy() {
		this.subscription.unsubscribe();
		this.ngUnsubscribe.next(true);
		this.ngUnsubscribe.complete();
	}

	//OVERRIDE in derived class
	getFilters(): Array<Filter> {
		return new Array<Filter>();
	}

	markPristine() {
		this.dataForm.markAsPristine();
	}

	hasFilters(): boolean {
		return this.dataForm.dirty;
	}

	//OVERRIDE in derived class
	resetToDefault() {}

	notifyFilterChanged() {
		this.filterChanged.emit();
	}

	toggleAllMultiSelectSelection(selection: MatOption, field: AbstractControl, lookupData: any[]) {
		toggleAllMultiSelectSelectionHelper(selection, field, lookupData);
	}

	checkIfMultiSelectHaveAllSelected(field: AbstractControl, lookupData: any[], matOption: MatOption) {
		if (field.value && field.value.includes(0)) {
			this.toggleAllMultiSelectSelection(matOption, field, lookupData);
		}
	}

	getMultiSelectDisplayValue(field: AbstractControl, lookupData: any[], displayValue: string): any {
		return getMultiSelectDisplayValueHelper(field, lookupData, displayValue);
	}

	multiSelectSelectionChanged(selected: boolean, field: AbstractControl, lookupData: any[]) {
		multiSelectSelectionChangedHelper(selected, field, lookupData);
	}

	toggleAllMultiSelectSelectionOnKeydown(event: KeyboardEvent, selection: MatOption, selectedOptions: MatOption[], field: AbstractControl, lookupData: any[], fieldName: string) {
		//applys the filters
		if (event.key === 'Enter') {
			this.callParentFilterMethod();
		}

		if (event.key === ' ') {
			const previousValue = this.previousValues.get(fieldName);
			const allValueChanged = previousValue != null && selection.selected !== previousValue;

			if (allValueChanged) {
				toggleAllMultiSelectSelectionHelper(selection, field, lookupData);
			} else {
				//checks if the 'All' option is selected
				var allOptionSelected = selectedOptions.some((option) => option.value === 0);

				//the 'All' option is not part of the lookupdata
				var allSelected = allOptionSelected ? selectedOptions.length - 1 === lookupData.length : selectedOptions.length === lookupData.length;

				if (allSelected) {
					this.toggleAllMultiSelectSelectionFromBooleanHelper(allSelected, field, lookupData);
				} else {
					field.setValue(field.value.filter((i) => i !== 0));
				}
			}
			this.previousValues.set(fieldName, selection.selected);
		}
	}

	toggleAllMultiSelectSelectionFromBooleanHelper(selectAll: boolean, field: AbstractControl, lookupData: any[]) {
		if (selectAll) {
			selectAllItems(field, lookupData);
		} else {
			field.patchValue([]);
		}
	}

	callParentFilterMethod() {
		this.listFilterIntermediaryService.callParentMethod('Message from child');
	}
	applyFilter() {
		// the filter button is disabled if !hasFilters(), this check is to prevent filtering on the enter key
		if (this.dataForm.valid && this.hasFilters()) {
			const filters = this.getFilters();
			this.filterChanged.emit(filters);
			this.markPristine();
		}
	}

	// Grouped MultiSelect Helpers
	checkIfGroupedMultiSelectHaveAllSelected(field: AbstractControl, lookupData: any[], matOption: MatOption) {
		if (field.value && field.value.includes(0)) {
			this.toggleAllGroupedMultiSelectSelection(matOption, field, lookupData);
		}
	}

	toggleAllGroupedMultiSelectSelection(selection: MatOption, field: AbstractControl, lookupData: any[]) {
		toggleAllMultiSelectSelectionGroupedHelper(selection, field, lookupData);
	}

	groupedMultiSelectSelectionChanged(selected: boolean, field: AbstractControl, lookupData: any[]) {
		groupedMultiSelectSelectionChangedHelper(selected, field, lookupData);
	}
	getGroupedMultiSelectDisplayValue(field: AbstractControl, lookupData: any[], displayValue: string): any {
		return getGroupedMultiSelectDisplayValueHelper(field, lookupData, displayValue);
	}
}
